// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the corner pop-up for the end-of-day wrap-up (design/evening-wrapup r2-popup): the same plan
// card as in the Konvo chat, acted on in place. Start or Not tonight count as used and the card stays
// up to show "Tonight is set"; "Open in Konvo chat" goes to the message.

import styles from '@app/features/konvo_corner/components/CornerCard.module.css';
import own from '@app/features/konvo_corner/components/CornerWrapUpCard.module.css';
import Corner from '@app/features/konvo_corner/state/Corner';
import {WrapUpCard} from '@app/features/konvo_wrapup/components/WrapUpCard';
import WrapUpStore from '@app/features/konvo_wrapup/state/WrapUpStore';
import {WRAP_UP_COPY as C, timeOf} from '@app/features/konvo_wrapup/utils/WrapUpCopy';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {XIcon} from '@app/features/ui/icons/Icons';
import type {CornerSuggestion} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconMessageCircle} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef} from 'react';

const REGION = msg({
	message: 'Wrap-up from Konvo',
	comment: 'Accessible name of the corner pop-up with the end-of-day wrap-up.',
});
const CLOSE = msg({message: 'Close', comment: 'Close the corner pop-up.'});

export const CornerWrapUpCard = observer(function CornerWrapUpCard({
	suggestion,
	variant,
	className,
}: {
	suggestion: CornerSuggestion;
	variant: 'desktop' | 'phone';
	className?: string;
}) {
	const {i18n} = useLingui();
	const planId = suggestion.action.context?.['plan_id'] ?? '';
	const plan = WrapUpStore.get(planId);
	const status = plan?.status;
	const first = useRef(status);
	// Started or skipped here (or in the chat while this is up): the pop-up was used.
	useEffect(() => {
		if (first.current === undefined) first.current = status;
		if (status && first.current === 'offered' && status !== 'offered')
			Corner.usedInPlace(status === 'skipped' ? 'skip' : 'start');
	}, [status]);
	const openChat = () => Corner.main();
	return (
		<section
			className={clsx(
				styles.card,
				variant === 'phone' ? styles.phone : [styles.desktop, own.wide],
				own.card,
				className,
			)}
			aria-label={i18n._(REGION)}
			aria-keyshortcuts="Alt+Shift+K"
			tabIndex={-1}
			onMouseEnter={() => Corner.hover(true)}
			onMouseLeave={() => Corner.hover(false)}
			data-kind={suggestion.kind}
			data-flx="konvo-corner.card"
		>
			<div className={styles.top}>
				<KonvoLogo size={18} />
				<span className={styles.context}>{i18n._(C.cornerContext, {time: timeOf(suggestion.due_at)})}</span>
				{suggestion.action.channel_id && (
					<button type="button" className={own.open} onClick={openChat} data-flx="konvo-corner.wrap-up.open-chat">
						<IconMessageCircle size={15} stroke={1.9} />
						<span>{i18n._(C.openInChat)}</span>
					</button>
				)}
				<button
					type="button"
					className={styles.iconButton}
					aria-label={i18n._(CLOSE)}
					onClick={() => Corner.dismiss()}
					data-flx="konvo-corner.card.close"
				>
					<XIcon size={16} />
				</button>
			</div>
			{status === 'offered' && (
				<p className={styles.title} data-flx="konvo-corner.card.title">
					{suggestion.title}
				</p>
			)}
			<div className={own.body}>
				<WrapUpCard planId={planId} where="corner" />
			</div>
		</section>
	);
});
